import React from 'react';
import { ChevronDown, Copy, User2, Sun, Moon, Settings2, Sparkles, ShieldCheck, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export interface AppHeaderProfileDropdownProps {
  profileDropdownRef: React.RefObject<HTMLDivElement | null>;
  user: any;
  userRole?: string | null;
  signOut: () => Promise<void>;
  showProfileDropdown: boolean;
  setShowProfileDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUserProfileModal: (show: boolean) => void;
  isDarkMode: boolean;
  setIsDarkMode: (dark: boolean | ((d: boolean) => boolean)) => void;
  syncProjectViews: boolean;
  setSyncProjectViews: (sync: boolean | ((s: boolean) => boolean)) => void;
  setShowCreditsAndHelpModal: (show: boolean) => void;
  setShowGuidedTour: (show: boolean) => void;
  setShowPrivacyModal: (show: boolean) => void;
}

export const AppHeaderProfileDropdown: React.FC<AppHeaderProfileDropdownProps> = ({
  profileDropdownRef,
  user,
  userRole,
  signOut,
  showProfileDropdown,
  setShowProfileDropdown,
  setShowUserProfileModal,
  isDarkMode,
  setIsDarkMode,
  syncProjectViews,
  setSyncProjectViews,
  setShowCreditsAndHelpModal,
  setShowGuidedTour,
  setShowPrivacyModal,
}) => {
  return (
    <div className="relative" ref={profileDropdownRef}>
      <button
        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
        className={`flex items-center gap-2 p-1.5 rounded-xl border transition-all active:scale-95 hover:bg-white/10 ${
          showProfileDropdown ? 'bg-white/10 border-indigo-500/50' : 'bg-white/5 border-white/10'
        }`}
        title="Benutzerprofil"
      >
        <div className="w-7 h-7 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md">
          {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
        </div>
        <span className="hidden md:inline text-xs font-semibold text-slate-300 max-w-[120px] truncate">
          {user?.email?.split('@')[0]}
        </span>
        <ChevronDown size={12} className={`text-slate-400 transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {showProfileDropdown && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 top-full mt-2 w-56 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-1.5 z-[100] backdrop-blur-xl space-y-0.5"
          >
            <div className="px-3 py-2 border-b border-white/10 mb-1">
              <p className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider">Angemeldet als</p>
              <div className="flex items-center justify-between gap-1.5 mt-0.5">
                <p className="text-xs font-bold text-white truncate">{user?.email}</p>
                <button
                  onClick={() => {
                    if (user?.email) {
                      navigator.clipboard.writeText(user.email);
                      alert('E-Mail zum Teilen in Zwischenablage kopiert!');
                    }
                  }}
                  className="p-1 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors shrink-0"
                  title="Freigabe-E-Mail in Zwischenablage kopieren"
                >
                  <Copy size={12} />
                </button>
              </div>
              {userRole === 'admin' && (
                <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-widest text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-md">Admin</span>
              )}
            </div>

            <button
              onClick={() => {
                setShowProfileDropdown(false);
                setShowUserProfileModal(true);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-indigo-500/10 text-indigo-300 rounded-xl transition-all group"
              title="Nutzer-Profil, Freigabe-ID, Nutzerverwaltung & Kontoeinstellungen"
            >
              <div className="w-6 h-6 rounded-md bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform shrink-0">
                <User2 size={14} />
              </div>
              <span className="text-xs font-bold truncate">Profilverwaltung</span>
            </button>

            <div className="h-px bg-white/5 mx-1 my-0.5" />

            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-white/5 text-slate-300 hover:text-white rounded-xl transition-all group"
              title="Zwischen hellem und dunklem Erscheinungsbild wechseln"
            >
              <div className="flex items-center gap-2.5 truncate">
                <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform shrink-0">
                  {isDarkMode ? <Sun size={14} className="text-amber-400" /> : <Moon size={14} />}
                </div>
                <span className="text-xs font-bold truncate">{isDarkMode ? 'Heller Modus' : 'Dunkler Modus'}</span>
              </div>
              <div className={`w-7 h-4 rounded-full p-0.5 transition-colors duration-200 focus:outline-none flex items-center shrink-0 ${
                isDarkMode ? 'bg-indigo-600' : 'bg-slate-700'
              }`}>
                <div className={`w-3 h-3 rounded-full bg-white shadow-md transition-transform duration-200 transform ${
                  isDarkMode ? 'translate-x-3' : 'translate-x-0'
                }`} />
              </div>
            </button>

            <button
              onClick={() => setSyncProjectViews(!syncProjectViews)}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-white/5 text-slate-300 hover:text-white rounded-xl transition-all group"
              title="Gleiche Ansicht bei Projektwechsel beibehalten"
            >
              <div className="flex items-center gap-2.5 truncate">
                <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform shrink-0">
                  <Settings2 size={14} />
                </div>
                <span className="text-xs font-bold truncate">Ansichten synchronisieren</span>
              </div>
              <div className={`w-7 h-4 rounded-full p-0.5 transition-colors duration-200 focus:outline-none flex items-center shrink-0 ${
                syncProjectViews ? 'bg-indigo-600' : 'bg-slate-700'
              }`}>
                <div className={`w-3 h-3 rounded-full bg-white shadow-md transition-transform duration-200 transform ${
                  syncProjectViews ? 'translate-x-3' : 'translate-x-0'
                }`} />
              </div>
            </button>

            <button
              onClick={() => {
                setShowProfileDropdown(false);
                setShowCreditsAndHelpModal(true);
              }}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-white/5 text-slate-300 hover:text-white rounded-xl transition-all group"
              title="Tastaturkürzel (Strg + K), Mitwirkende & Technologien"
            >
              <div className="flex items-center gap-2.5 truncate">
                <div className="w-6 h-6 rounded-md bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform shrink-0">
                  <Sparkles size={14} className="text-amber-400" />
                </div>
                <span className="text-xs font-bold truncate">Credits & Hilfe</span>
              </div>
              <span className="px-1.5 py-0.2 bg-indigo-500/20 text-indigo-300 text-[9px] font-mono rounded shrink-0">Strg+K</span>
            </button>

            <button
              onClick={() => {
                setShowProfileDropdown(false);
                setShowGuidedTour(true);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-indigo-500/10 text-indigo-300 rounded-xl transition-all group"
              title="Interaktive Einführungstour erneut starten"
            >
              <div className="w-6 h-6 rounded-md bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform shrink-0">
                <Sparkles size={14} />
              </div>
              <span className="text-xs font-bold truncate">Willkommenstour</span>
            </button>

            <button
              onClick={() => {
                setShowProfileDropdown(false);
                setShowPrivacyModal(true);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-emerald-500/10 text-slate-300 hover:text-white rounded-xl transition-all group"
              title="DSGVO-Erklärung & Nutzerrechte"
            >
              <div className="w-6 h-6 rounded-md bg-emerald-500/10 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform shrink-0">
                <ShieldCheck size={14} />
              </div>
              <span className="text-xs font-bold truncate">Datenschutz & Privatsphäre</span>
            </button>

            <div className="h-px bg-white/5 mx-1 my-0.5" />

            <button
              onClick={async () => {
                setShowProfileDropdown(false);
                await signOut();
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-rose-500/10 hover:text-rose-400 text-rose-300 rounded-xl transition-all group"
              title="Aktuelle Sitzung beenden"
            >
              <div className="w-6 h-6 rounded-md bg-rose-500/10 flex items-center justify-center text-rose-400 group-hover:scale-110 transition-transform shrink-0">
                <LogOut size={14} />
              </div>
              <span className="text-xs font-bold truncate">Abmelden</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
